/**
 * Contract Checker — Upload & Setup screen (Step 1 of 2).
 *
 * Pure presentational orchestrator — collects the form inputs, gates
 * the Start CTA on the required combo (file or prefill + role), and
 * hands them off to the parent via `onSubmit`. The parent (the
 * `/new` page) owns the text-extraction + AI call + UDS persistence.
 *
 * Two modes:
 *  - **Upload mode** (default): renders `<FileDropZone>`; user picks a
 *    PDF from disk.
 *  - **Prefill mode** (`prefill` prop set): renders
 *    `<PrefilledDocumentBadge>` instead; the document text is already
 *    in hand, no upload needed.
 */

import { Button } from '@/components/ui-kit/button';
import { Loader2, ShieldCheck } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FileDropZone } from './file-drop-zone';
import { PrefilledDocumentBadge } from './prefilled-document-badge';
import { RoleSelector, type ContractRole } from './role-selector';
import { ValuePropCard } from './value-prop-card';

export interface UploadAndSetupPrefill {
  contractId: string;
  contractTitle: string;
}

export interface UploadAndSetupSubmitArgs {
  file: File | null;
  role: ContractRole;
  topics: string;
}

interface UploadAndSetupProps {
  /** When set, the file dropzone is hidden and we show the prefill badge. */
  prefill?: UploadAndSetupPrefill;
  /** Where the "Change" link on the prefill badge should point. */
  changeHref: string;
  /** Called with the collected inputs when the user clicks Start. */
  onSubmit: (args: UploadAndSetupSubmitArgs) => void | Promise<void>;
  /** When true, the Start CTA shows a spinner and gates further clicks. */
  isSubmitting: boolean;
}

export function UploadAndSetup({
  prefill,
  changeHref,
  onSubmit,
  isSubmitting,
}: UploadAndSetupProps) {
  const { t } = useTranslation();
  const [file, setFile] = useState<File | null>(null);
  const [role, setRole] = useState<ContractRole | null>(null);
  const [topics, setTopics] = useState('');

  // In prefill mode the parent already holds the document text, so we
  // don't require a File. In upload mode we do.
  const hasDocument = Boolean(prefill) || Boolean(file);
  const canStart = hasDocument && !!role && !isSubmitting;

  const handleStartAnalysis = () => {
    if (!canStart || !role) return;
    void onSubmit({ file, role, topics });
  };

  return (
    <div className="relative w-full">
      <DecorativeBackground />

      <div className="relative flex flex-col items-center px-4 py-6 sm:py-10">
        {/* <StepIndicator /> */}

        <div className="w-full max-w-200 bg-card border border-border rounded-2xl p-5 sm:p-8 md:p-12 shadow-[0_4px_20px_rgba(0,31,53,0.08)]">
          <header className="mb-6 sm:mb-10 text-center">
            <h2 className="text-xl sm:text-2xl font-semibold mb-2 text-[hsl(var(--fg))] font-heading">
              {t('ORGCONTRACTCHECKER_CARDTITLE', { defaultValue: 'Analyze Your Agreement' })}
            </h2>
            <p className="text-sm sm:text-base text-globe-grey">
              {t('ORGCONTRACTCHECKER_CARDDESCRIPTION', {
                defaultValue:
                  'Upload your contract to identify risks and negotiation opportunities.',
              })}
            </p>
          </header>

          <div className="grid grid-cols-1 md:grid-cols-12 gap-6 md:gap-8 lg:gap-10">
            {prefill ? (
              <PrefilledDocumentBadge
                contractTitle={prefill.contractTitle}
                changeHref={changeHref}
              />
            ) : (
              <FileDropZone file={file} onFile={setFile} />
            )}

            <div className="md:col-span-5 flex flex-col gap-5 sm:gap-6">
              <RoleSelector value={role} onChange={setRole} />
              <ValuePropCard />
            </div>
          </div>

          <TopicsInput value={topics} onChange={setTopics} />

          <SubmitSection
            canStart={canStart}
            isSubmitting={isSubmitting}
            onStart={handleStartAnalysis}
          />
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Local helpers — inlined because they're purely presentational, used once,
// and tightly coupled to the page's outer container.
// ---------------------------------------------------------------------------

function DecorativeBackground() {
  return (
    <div
      className="absolute inset-0 -z-10 overflow-hidden rounded-2xl pointer-events-none"
      aria-hidden
    >
      <div className="absolute -top-24 -left-24 w-[40%] h-[40%] rounded-full bg-selise-blue/10 blur-[120px]" />
      <div className="absolute -bottom-24 -right-24 w-[40%] h-[40%] rounded-full bg-selise-blue/5 blur-[120px]" />
    </div>
  );
}

function StepIndicator() {
  const { t } = useTranslation();
  return (
    <div className="mb-6 sm:mb-8 flex flex-col items-center">
      <h1 className="text-lg sm:text-xl font-bold text-selise-blue mb-3 font-heading">
        {t('ORGCONTRACTCHECKER_TITLE', { defaultValue: 'Contract Checker' })}
      </h1>
      <div className="flex items-center gap-2" aria-hidden>
        <span className="w-2 h-2 rounded-full bg-selise-blue" />
        <span className="w-12 h-px bg-border" />
        <span className="w-2 h-2 rounded-full bg-border" />
      </div>
    </div>
  );
}

interface TopicsInputProps {
  value: string;
  onChange: (next: string) => void;
}

function TopicsInput({ value, onChange }: TopicsInputProps) {
  const { t } = useTranslation();
  return (
    <div className="mt-6 sm:mt-8">
      <label
        htmlFor="contract-checker-topics"
        className="block mb-2 text-[11px] font-semibold uppercase tracking-wider text-globe-grey"
      >
        {t('ORGCONTRACTCHECKER_LABEL_TOPICS', {
          defaultValue: 'Is there any specific topics you want to check',
        })}
      </label>
      <textarea
        id="contract-checker-topics"
        value={value}
        onChange={(event) => onChange(event.target.value)}
        placeholder={t('ORGCONTRACTCHECKER_TOPICS_PLACEHOLDER', {
          defaultValue: 'Example: Financial, Ownership, Violation etc.',
        })}
        rows={3}
        className="w-full rounded-lg border border-border bg-transparent px-3 py-2 text-sm placeholder:text-globe-grey focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-selise-blue/40 resize-y min-h-20"
      />
    </div>
  );
}

interface SubmitSectionProps {
  canStart: boolean;
  isSubmitting: boolean;
  onStart: () => void;
}

function SubmitSection({ canStart, isSubmitting, onStart }: SubmitSectionProps) {
  const { t } = useTranslation();
  return (
    <div className="mt-6 sm:mt-8">
      <div className="flex items-center gap-2 mb-3 sm:mb-4 text-globe-grey">
        <ShieldCheck className="w-4 h-4 shrink-0" />
        <span className="text-[11px] sm:text-xs">
          {t('ORGCONTRACTCHECKER_TRUST', {
            defaultValue: 'Bank-grade 256-bit encryption',
          })}
        </span>
      </div>
      <Button
        type="button"
        variant="primary"
        className="w-full"
        disabled={!canStart}
        onClick={onStart}
      >
        {isSubmitting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
        {isSubmitting
          ? t('ORGCONTRACTCHECKER_STARTING', { defaultValue: 'Starting…' })
          : t('ORGCONTRACTCHECKER_START', { defaultValue: 'Start AI Analysis' })}
      </Button>
    </div>
  );
}

export default UploadAndSetup;
